import React from 'react';
import { Clock } from 'lucide-react';

/**
 * Timeline Visualizer Component
 * Visual representation of practice session timeline with color-coded segments
 */
export default function TimelineVisualizer({ workflow, totalDuration, targetDuration }) {
  
  const getToolColor = (toolId) => {
    const colors = {
      'breath-coach': 'bg-cyan-400',
      'pitch-coach': 'bg-purple-400',
      'audition-songs': 'bg-orange-400'
    };
    return colors?.[toolId] || 'bg-gray-400';
  };

  const getToolName = (toolId) => {
    const names = {
      'breath-coach': 'Breath',
      'pitch-coach': 'Pitch',
      'audition-songs': 'Songs'
    };
    return names?.[toolId] || 'Unknown';
  };

  const calculatePercentage = (duration) => {
    if (totalDuration === 0) return 0;
    return (duration / totalDuration) * 100;
  };

  const isDurationValid = totalDuration >= targetDuration * 0.8 && totalDuration <= targetDuration * 1.2;

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
      <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <Clock className="w-5 h-5" />
        Practice Timeline
      </h2>

      {/* Duration Status */}
      <div className="mb-4 flex items-center justify-between">
        <div className="text-white/80 text-sm">
          Total: <span className="font-bold text-white">{totalDuration} min</span> / 
          Target: <span className="font-bold text-white">{targetDuration} min</span>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium ${
          isDurationValid 
            ? 'bg-green-500/20 text-green-200 border border-green-500/30' :'bg-amber-500/20 text-amber-200 border border-amber-500/30'
        }`}>
          {isDurationValid ? '✓ On Target' : '⚠ Adjust Duration'}
        </div>
      </div>

      {/* Timeline Visualization */}
      {workflow?.length === 0 ? (
        <div className="bg-white/5 rounded-xl p-6 text-center">
          <p className="text-white/60 text-sm">
            Timeline will appear here once you add tools to your workflow
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Visual Timeline Bar */}
          <div className="relative h-12 bg-white/5 rounded-xl overflow-hidden">
            {workflow?.map((segment, index) => {
              const percentage = calculatePercentage(segment?.duration);
              const color = getToolColor(segment?.tool);
              
              return (
                <div
                  key={segment?.id}
                  className={`absolute h-full ${color} transition-all`}
                  style={{
                    left: `${workflow?.slice(0, index)?.reduce((sum, s) => sum + calculatePercentage(s?.duration), 0)}%`,
                    width: `${percentage}%`
                  }}
                  title={`${getToolName(segment?.tool)}: ${segment?.duration} min`}
                />
              );
            })}
          </div>

          {/* Timeline Breakdown */}
          <div className="space-y-2">
            {workflow?.map((segment, index) => {
              const color = getToolColor(segment?.tool);
              const percentage = calculatePercentage(segment?.duration);
              
              return (
                <div key={segment?.id} className="flex items-center gap-3 text-sm">
                  <div className="flex items-center gap-2 flex-1">
                    <div className={`w-3 h-3 rounded-sm ${color}`} />
                    <span className="text-white/60">#{index + 1}</span>
                    <span className="text-white font-medium">{getToolName(segment?.tool)}</span>
                  </div>
                  <div className="text-white/80">
                    {segment?.duration} min
                  </div>
                  <div className="text-white/60 w-16 text-right">
                    {percentage?.toFixed(1)}%
                  </div>
                </div>
              );
            })}
          </div>

          {/* Cumulative Timing */}
          <div className="border-t border-white/10 pt-4">
            <h4 className="text-white/80 text-sm font-medium mb-2">Cumulative Timing</h4>
            <div className="space-y-1">
              {workflow?.map((segment, index) => {
                const cumulativeTime = workflow?.slice(0, index + 1)?.reduce((sum, s) => sum + s?.duration, 0);
                return (
                  <div key={segment?.id} className="flex items-center justify-between text-sm">
                    <span className="text-white/60">After segment #{index + 1}</span>
                    <span className="text-white font-medium">{cumulativeTime} min</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}